// client/src/pages/seo/DigitaleGetraenkekarteBarDe.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "Eine Getränkekarte sollte schnell, klar und aktuell bleiben",
  },
  {
    id: "why",
    label: "Warum ist eine digitale Getränkekarte für Bars sinnvoll?",
  },
  {
    id: "use-cases",
    label: "Welche Getränke funktionieren besonders gut in einer digitalen Karte?",
  },
  {
    id: "structure",
    label: "Wie sollte eine digitale Getränkekarte aufgebaut sein?",
  },
  {
    id: "updates",
    label: "Was kann eine Bar online schneller aktualisieren?",
  },
  {
    id: "mistakes",
    label: "Welche Fehler passieren bei digitalen Getränkekarten?",
  },
  {
    id: "recommendation",
    label: "Wie würde ich als Barinhaber starten?",
  },
  {
    id: "next-steps",
    label: "Was sollten Sie jetzt tun?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place hilft, eine digitale Getränkekarte zu erstellen",
  },
  {
    id: "faq",
    label: "FAQ zu digitalen Getränkekarten",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitaleGetraenkekarteBarDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Digitale Getränkekarte für Bars: Cocktails, Wein und Getränke online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/digital-drinks-menu-bar",
          de: "/de/digitale-getraenkekarte-bar",
          no: "/no/digital-drikkemeny-bar",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digitale Getränkekarte für Bars: Cocktails, Wein und Getränke
              online verwalten
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine Getränkekarte ändert sich oft häufiger als eine klassische
                Speisekarte: neue Cocktails, saisonale Getränke, nicht
                verfügbare Weine, alkoholfreie Optionen, aktualisierte Preise
                oder zeitlich begrenzte Angebote. Genau hier wird eine digitale
                Getränkekarte praktisch.
              </p>

              <p className="seoHero__text">
                Statt die Karte jedes Mal neu zu drucken oder eine PDF-Datei zu
                ersetzen, kann die Bar Getränke online aktualisieren. Der
                QR-Code bleibt gleich, während Getränke, Preise, Beschreibungen
                und Verfügbarkeit geändert werden können.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>Eine Getränkekarte sollte schnell, klar und aktuell bleiben</h2>

          <p>
            Getränke ändern sich oft kurzfristig. Ein Wein ist nicht mehr
            verfügbar, ein Cocktail wird für die Saison ersetzt, ein neues
            alkoholfreies Getränk wird ergänzt oder die Bar möchte ausgewählte
            Empfehlungen hervorheben.
          </p>

          <p>
            Eine digitale Getränkekarte hilft, diese Informationen aktuell zu
            halten, ohne immer wieder neu zu drucken oder Dateien zu ersetzen.
            Die Bar kann Getränke online aktualisieren, während Gäste weiterhin
            denselben QR-Code nutzen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Schwächer</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Gedruckte Getränkekarte wird schnell veraltet.
                </li>
                <li className="text__seo-li">
                  Nicht verfügbare Getränke bleiben sichtbar.
                </li>
                <li className="text__seo-li">
                  Neue Getränke erscheinen zu spät in der Karte.
                </li>
                <li className="text__seo-li">
                  Mehrere PDF-Versionen sind im Umlauf.
                </li>
                <li className="text__seo-li">
                  Das Personal erklärt ständig, was verfügbar ist.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besser</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Getränke online aktualisieren.</li>
                <li className="text__seo-li">
                  Verfügbare Getränke klar anzeigen.
                </li>
                <li className="text__seo-li">
                  Saisonale Angebote schnell hinzufügen.
                </li>
                <li className="text__seo-li">
                  Eine zentrale digitale Karte nutzen.
                </li>
                <li className="text__seo-li">Denselben QR-Code behalten.</li>
              </ul>
            </div>
          </div>

          <h3>Starten Sie mit einer kleinen digitalen Getränkekarte</h3>

          <p>
            Fügen Sie zuerst Cocktails, Weine oder alkoholfreie Empfehlungen
            hinzu und prüfen Sie die QR-Code-Ansicht auf dem Smartphone. Eine
            fokussierte erste Version lässt sich leichter testen als eine lange
            Getränkeliste mit jeder möglichen Kategorie.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum ist eine digitale Getränkekarte für Bars sinnvoll?</h2>

            <p>
              Eine klassische Getränkekarte funktioniert gut, wenn das Angebot
              stabil bleibt. Viele Bars, Cafés und Restaurants ändern Getränke
              aber je nach Saison, Einkauf, Verfügbarkeit, Tageszeit oder
              Konzept.
            </p>

            <p>
              Eine digitale Getränkekarte ist besonders nützlich, wenn Gäste
              aktuelle Optionen schnell verstehen sollen: Cocktails, Weine,
              Craft-Getränke, alkoholfreie Alternativen, Kaffeespezialitäten,
              Aperitifs, Digestifs oder Empfehlungen des Hauses.
            </p>

            <h3>Technologie sollte Service und Abläufe verbessern</h3>

            <p>
              Eine digitale Getränkekarte ist sinnvoll, wenn sie der Bar hilft,
              aktuelle Optionen klarer zu präsentieren, veraltete Informationen
              zu reduzieren und das Personal im Service zu unterstützen.
            </p>

            <p>
              Sie sollte guten Service nicht ersetzen. Bei Wein, Cocktails oder
              Empfehlungen kann sie das Gespräch zwischen Gast und Personal
              vorbereiten und unterstützen.
            </p>
          </div>
        </section>

        <section id="use-cases" className="acquaintance">
          <h2>
            Welche Getränke funktionieren besonders gut in einer digitalen Karte?
          </h2>

          <p>
            Nicht jede Bar braucht eine komplexe digitale Struktur. Die meisten
            Bars können aber davon profitieren, wenn häufig wechselnde oder
            erklärungsbedürftige Getränke klarer dargestellt werden.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Cocktailkarte.</li>
            <li className="text__seo-li">Weinkarte.</li>
            <li className="text__seo-li">Alkoholfreie Getränke.</li>
            <li className="text__seo-li">Saisonale Getränke.</li>
            <li className="text__seo-li">Happy-Hour-Angebote.</li>
            <li className="text__seo-li">Kaffee und Spezialitäten.</li>
            <li className="text__seo-li">Empfehlungen des Hauses.</li>
            <li className="text__seo-li">Nicht verfügbare Getränke.</li>
          </ul>

          <p>
            Eine digitale Getränkekarte ist besonders stark, wenn sie kurze
            Orientierung gibt: Geschmack, Stil, Intensität, alkoholfreie Option
            oder saisonaler Kontext.
          </p>

          <VisualIdea>
            Smartphone mit einer digitalen Getränkekarte: Cocktails, Weine,
            alkoholfreie Getränke und saisonale Empfehlungen als klare
            Kategorien.
          </VisualIdea>
        </section>

        <section id="structure" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wie sollte eine digitale Getränkekarte aufgebaut sein?</h2>

            <p>
              Eine gute Getränkekarte braucht Ordnung. Gäste sollten nicht durch
              eine lange gemischte Liste scrollen müssen, wenn sie einen
              Cocktail, ein Glas Wein oder eine alkoholfreie Empfehlung suchen.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Kategorien klar trennen:</strong> Zum Beispiel
                Cocktails, Wein, Bier, alkoholfrei, Kaffee und Empfehlungen.
              </li>

              <li className="text__seo-li">
                <strong>Kurze Beschreibungen nutzen:</strong> Geschmack, Stil
                oder besondere Zutaten kurz erklären.
              </li>

              <li className="text__seo-li">
                <strong>Preise sichtbar halten:</strong> Gäste sollten nicht
                danach suchen müssen.
              </li>

              <li className="text__seo-li">
                <strong>Verfügbarkeit verwalten:</strong> Nicht verfügbare
                Getränke ausblenden oder klar kennzeichnen.
              </li>

              <li className="text__seo-li">
                <strong>Empfehlungen hervorheben:</strong> Nicht jedes Getränk
                braucht die gleiche visuelle Gewichtung.
              </li>
            </ol>

            <p>
              In einer digitalen Karte kann diese Struktur später angepasst
              werden, ohne neue Druckkosten zu verursachen.
            </p>
          </div>
        </section>

        <section id="updates" className="acquaintance">
          <h2>Was kann eine Bar online schneller aktualisieren?</h2>

          <p>
            Der größte Vorteil liegt nicht nur in der Präsentation, sondern in
            der Pflege. Eine Bar kann Inhalte aktualisieren, ohne jedes Mal eine
            neue Getränkekarte drucken zu müssen.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Preise ändern.</li>
            <li className="text__seo-li">Neue Cocktails hinzufügen.</li>
            <li className="text__seo-li">Nicht verfügbare Weine ausblenden.</li>
            <li className="text__seo-li">
              Alkoholfreie Getränke hervorheben.
            </li>
            <li className="text__seo-li">Saisonale Angebote erstellen.</li>
            <li className="text__seo-li">Happy-Hour-Zeiten hinzufügen.</li>
            <li className="text__seo-li">Beschreibungen verbessern.</li>
            <li className="text__seo-li">Fotos ersetzen.</li>
          </ul>

          <h3>QR-Codes können die Getränkeberatung ergänzen</h3>

          <p>
            Für Bars muss die digitale Karte nicht jede persönliche Empfehlung
            ersetzen. Sie kann nützliche Details ergänzen, ohne die gedruckte
            Karte zu überladen.
          </p>

          <p>
            Das ist besonders hilfreich bei erklärungsbedürftigen Getränken wie
            Weinen, Cocktails, saisonalen Getränken oder alkoholfreien
            Alternativen.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Fehler passieren bei digitalen Getränkekarten?</h2>

            <p>
              Eine digitale Getränkekarte kann sehr nützlich sein, sollte aber
              nicht unübersichtlich, veraltet oder zu werblich werden.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zu viele Getränke ohne klare Kategorien.
                  </li>
                  <li className="text__seo-li">
                    Nicht verfügbare Weine oder Getränke bleiben sichtbar.
                  </li>
                  <li className="text__seo-li">
                    Happy-Hour-Angebote sind zeitlich unklar.
                  </li>
                  <li className="text__seo-li">
                    Beschreibungen sind zu lang oder zu poetisch.
                  </li>
                  <li className="text__seo-li">
                    Alkoholfreie Alternativen sind versteckt.
                  </li>
                  <li className="text__seo-li">
                    Preise werden nicht aktuell gehalten.
                  </li>
                  <li className="text__seo-li">
                    Der QR-Code ersetzt die Beratung durch das Personal komplett.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Klare Kategorien verwenden.</li>
                  <li className="text__seo-li">
                    Nicht verfügbare Getränke sofort ausblenden.
                  </li>
                  <li className="text__seo-li">
                    Zeitlich begrenzte Angebote klar kennzeichnen.
                  </li>
                  <li className="text__seo-li">
                    Kurze, nützliche Beschreibungen schreiben.
                  </li>
                  <li className="text__seo-li">
                    Alkoholfreie Optionen sichtbar machen.
                  </li>
                  <li className="text__seo-li">
                    Preise regelmäßig prüfen.
                  </li>
                  <li className="text__seo-li">
                    Die digitale Karte als Service-Ergänzung nutzen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Wie würde ich als Barinhaber starten?</h2>

          <p>
            Ich würde nicht sofort die gesamte Getränkekarte digitalisieren. Ich
            würde mit dem Bereich starten, der sich am häufigsten ändert oder am
            meisten Erklärung braucht.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Eine Hauptkategorie wählen:</strong> Zum Beispiel
              Cocktails, Weine oder alkoholfreie Getränke.
            </li>

            <li className="text__seo-li">
              <strong>10 bis 20 Getränke hinzufügen:</strong> Mit Preis und
              kurzer Beschreibung.
            </li>

            <li className="text__seo-li">
              <strong>Empfehlungen markieren:</strong> Nicht jedes Getränk
              sollte gleich wichtig wirken.
            </li>

            <li className="text__seo-li">
              <strong>Auf dem Smartphone prüfen:</strong> Die Karte muss schnell
              und gut lesbar sein.
            </li>

            <li className="text__seo-li">
              <strong>Verfügbarkeit testen:</strong> Ein Getränk ausblenden und
              später wieder aktivieren.
            </li>

            <li className="text__seo-li">
              <strong>Danach erweitern:</strong> Weitere Kategorien erst nach
              dem ersten echten Test hinzufügen.
            </li>
          </ol>

          <p>
            So bleibt die Getränkekarte überschaubar und wird nicht zu einer
            langen, schwer lesbaren Liste.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollten Sie jetzt tun?</h2>

            <p>
              Wenn Sie eine digitale Getränkekarte für Ihre Bar erstellen
              möchten, starten Sie mit einem kleinen Test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Eine digitale Karte erstellen.</li>
              <li className="text__seo-li">Eine Getränkekategorie hinzufügen.</li>
              <li className="text__seo-li">
                10 bis 20 echte Getränke eintragen.
              </li>
              <li className="text__seo-li">
                Kurze Beschreibungen und Preise hinzufügen.
              </li>
              <li className="text__seo-li">
                Die Karte über den QR-Code auf dem Smartphone öffnen.
              </li>
              <li className="text__seo-li">
                Ein nicht verfügbares Getränk testweise ausblenden.
              </li>
              <li className="text__seo-li">
                Danach Cocktails, Wein oder alkoholfreie Optionen erweitern.
              </li>
            </ul>

            <VisualIdea>
              Ein kleiner Bar-Kartentest: eine Getränkekategorie, 10 echte
              Getränke, sichtbare Preise, kurze Beschreibungen und ein nicht
              verfügbares Getränk, das in der öffentlichen Karte ausgeblendet
              ist.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ zu digitalen Getränkekarten</h2>

          <div className="seoFaq">
            <details>
              <summary>Was ist eine digitale Getränkekarte?</summary>
              <p>
                Das ist eine online verwaltete Karte für Cocktails, Wein,
                alkoholfreie Getränke, Kaffee, Bier oder saisonale Angebote, die
                Gäste per QR-Code öffnen können.
              </p>
            </details>

            <details>
              <summary>
                Ist eine digitale Getränkekarte nur für Bars sinnvoll?
              </summary>
              <p>
                Nein. Sie kann auch Restaurants, Cafés, Hotels, Weinbars,
                Cocktailbars oder Eventlocations helfen.
              </p>
            </details>

            <details>
              <summary>Muss ich die gedruckte Getränkekarte entfernen?</summary>
              <p>
                Nicht unbedingt. Eine Kombination funktioniert oft am besten:
                gedruckte Karte für den schnellen Überblick und QR-Code für
                aktuelle oder detaillierte Informationen.
              </p>
            </details>

            <details>
              <summary>Kann ich nicht verfügbare Getränke ausblenden?</summary>
              <p>
                Ja. Nicht verfügbare Getränke können vorübergehend ausgeblendet
                und später wieder aktiviert werden.
              </p>
            </details>

            <details>
              <summary>Kann ich alkoholfreie Optionen hervorheben?</summary>
              <p>
                Ja. Eine digitale Karte ist nützlich, um alkoholfreie Getränke,
                saisonale Getränke oder Empfehlungen sichtbarer zu machen.
              </p>
            </details>

            <details>
              <summary>Bleibt der QR-Code gleich?</summary>
              <p>
                Ja, wenn der QR-Code auf einen stabilen Menü-Link führt. Sie
                können Inhalte aktualisieren, ohne den QR-Code neu zu drucken.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="menu"
          exclude="/de/digitale-getraenkekarte-bar"
        />
      </article>
    </main>
  );
}